import { type TipTapDoc, emptyTipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { VENDOR_CATEGORIES, sanitizeWebsite as sanitizeWebsiteUtil } from "@compass/shared/utils";
import * as Dialog from "@radix-ui/react-dialog";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { type Editor } from "@tiptap/react";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import { Button } from "components/elements";
import { PostEditor } from "components/form/PostEditor";
import { trpc } from "lib/trpc";
import { useStore } from "@tanstack/react-store";
import { VendorListing } from "@compass/shared/types";
import { NewVendorSchema } from "@compass/shared/types";
import { VendorAddedModal } from "./VendorAddedModal";

// notesContentJson is held as local state (not form state) because Zod's
// inferred type for the recursive TipTapDocSchema collapses to `unknown[]`,
// which conflicts with the canonical `TipTapDoc` type when threaded through
// react-form's StandardSchema validator.
const NewVendorFormSchema = NewVendorSchema.omit({ notesContentJson: true });
type NewVendorFormValues = z.infer<typeof NewVendorFormSchema>;

export const sanitizeWebsite = (input: string): string => {
    return sanitizeWebsiteUtil(input) || "";
};

export const NewVendorForm = ({
    isOpen,
    onClose,
    editForm,
}: {
    isOpen: boolean;
    onClose: () => void;
    editForm: VendorListing | null;
}) => {
    const navigate = useNavigate();
    const utils = trpc.useContext();

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [customError, setCustomError] = useState<string | null>(null);
    const [notesContentJson, setNotesContentJson] = useState<TipTapDoc>(() => emptyTipTapDoc());

    const [editor, setEditor] = useState<Editor | null>(null);
    const editorRef = useCallback((instance: Editor | null) => {
        if (instance) setEditor(instance);
    }, []);

    const { isPending: isLoading, mutateAsync: createVendor } =
        trpc.vendors.submitVendor.useMutation({
            onSuccess: () => {
                utils.vendors.latest.invalidate();
                navigate({ to: "/vendors" });
                onClose();
            },
        });

    const { isPending: isEditing, mutateAsync: updateVendor } = trpc.vendors.editVendor.useMutation(
        {
            onSuccess: () => {
                utils.vendors.latest.invalidate();
                navigate({ to: "/vendors" });
                onClose();
            },
        },
    );

    const form = useForm({
        defaultValues: {
            name: "",
            website: "",
            contactName: "",
            contactEmail: "",
            contactAddress: "",
            category: "",
        } as NewVendorFormValues,
        validators: {
            onChange: NewVendorFormSchema,
        },
        onSubmit: async ({ value }) => {
            const sanitizedWebsite = sanitizeWebsite(value.website);
            const payload = { ...value, website: sanitizedWebsite, notesContentJson };

            try {
                if (editForm) {
                    await updateVendor({ id: editForm.id, data: payload });
                } else {
                    await createVendor(payload);
                    setShowSuccessModal(true);
                }
                setCustomError(null);
            } catch (err: any) {
                if (err.message?.includes("already exists")) {
                    setCustomError(err.message);
                } else {
                    console.error("Vendor submission error", err);
                }
            }
        },
    });

    const name = useStore(form.store, (state) => state.values.name);
    const website = useStore(form.store, (state) => state.values.website);
    const category = useStore(form.store, (state) => state.values.category);
    const isValid = useStore(form.store, (state) => state.isValid);

    useEffect(() => {
        if (editForm) {
            form.reset();
            form.setFieldValue("name", editForm.name);
            form.setFieldValue("website", editForm.website || "");
            form.setFieldValue("contactName", editForm.contactName || "");
            form.setFieldValue("contactEmail", editForm.contactEmail || "");
            form.setFieldValue("contactAddress", editForm.contactAddress || "");
            form.setFieldValue("category", editForm.category || "");
            const initialDoc = (editForm.notesContentJson as TipTapDoc | null) ?? emptyTipTapDoc();
            setNotesContentJson(initialDoc);

            setTimeout(() => {
                editor?.commands.setContent(initialDoc);
                editor?.commands.focus();
            }, 0);
        } else {
            form.reset();
            setNotesContentJson(emptyTipTapDoc());
        }
    }, [editForm, isOpen, editor]);

    return (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
                <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="max-h-[95vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-none">
                        <div className="flex items-center justify-between">
                            <Dialog.Title className="text-left align-middle font-sans text-[24px] font-bold leading-[100%] tracking-[0] text-[#424242]">
                                {editForm ? "Edit" : "Add New"} Vendor
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button>
                                    <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                                </button>
                            </Dialog.Close>
                        </div>
                        <hr className="-mx-[20px] my-4 border-[#c8c8c8]" />

                        <h3 className="mb-4 font-sans text-[20px] font-semibold leading-[100%] tracking-[0] text-[#424242]">
                            Vendors information
                        </h3>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                form.handleSubmit();
                            }}
                            className="space-y-4"
                        >
                            <div className="flex items-center gap-3">
                                <label className="w-[45%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Company Name <span className="text-red-400">*</span>
                                </label>
                                <div className="flex w-full flex-col">
                                    <form.Field name="name">
                                        {(field) => (
                                            <>
                                                <input
                                                    name={field.name}
                                                    value={field.state.value}
                                                    onBlur={field.handleBlur}
                                                    onChange={(e) =>
                                                        field.handleChange(e.target.value)
                                                    }
                                                    className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                                    placeholder="Company Name"
                                                />
                                                {field.state.meta.errors.length > 0 && (
                                                    <p className="text-xs text-red-400">
                                                        {field.state.meta.errors[0]?.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </form.Field>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[45%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Website <span className="text-red-400">*</span>
                                </label>
                                <div className="flex w-full flex-col">
                                    <form.Field name="website">
                                        {(field) => (
                                            <>
                                                <input
                                                    name={field.name}
                                                    value={field.state.value}
                                                    onBlur={field.handleBlur}
                                                    onChange={(e) =>
                                                        field.handleChange(e.target.value)
                                                    }
                                                    className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                                    placeholder="https://example.com"
                                                />
                                                {field.state.meta.errors.length > 0 && (
                                                    <p className="mt-1 text-xs text-red-400">
                                                        {field.state.meta.errors[0]?.message}
                                                    </p>
                                                )}
                                                {customError && (
                                                    <p className="mt-1 text-xs text-red-400">
                                                        {customError}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </form.Field>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[45%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Contact Name
                                </label>
                                <form.Field name="contactName">
                                    {(field) => (
                                        <input
                                            name={field.name}
                                            value={field.state.value ?? ""}
                                            onBlur={field.handleBlur}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                            className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                            placeholder="Contact Name"
                                        />
                                    )}
                                </form.Field>
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[45%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Contact Email
                                </label>
                                <div className="flex w-full flex-col">
                                    <form.Field name="contactEmail">
                                        {(field) => (
                                            <>
                                                <input
                                                    name={field.name}
                                                    value={field.state.value}
                                                    onBlur={field.handleBlur}
                                                    onChange={(e) =>
                                                        field.handleChange(e.target.value)
                                                    }
                                                    className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                                    placeholder="Contact Email"
                                                />
                                                {field.state.meta.errors.length > 0 && (
                                                    <p className="text-xs text-red-400">
                                                        {field.state.meta.errors[0]?.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </form.Field>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[45%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Contact Address
                                </label>
                                <form.Field name="contactAddress">
                                    {(field) => (
                                        <input
                                            name={field.name}
                                            value={field.state.value ?? ""}
                                            onBlur={field.handleBlur}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                            className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                            placeholder="Contact Address"
                                        />
                                    )}
                                </form.Field>
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[45%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Category <span className="text-red-400">*</span>
                                </label>
                                <div className="flex w-full flex-col">
                                    <form.Field name="category">
                                        {(field) => (
                                            <>
                                                <select
                                                    name={field.name}
                                                    value={field.state.value}
                                                    onBlur={field.handleBlur}
                                                    onChange={(e) =>
                                                        field.handleChange(e.target.value)
                                                    }
                                                    className={`focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                                                        category ? "text-black" : "text-gray-400"
                                                    }`}
                                                >
                                                    <option value="">Select a category</option>
                                                    {VENDOR_CATEGORIES.map((cat) => (
                                                        <option key={cat} value={cat}>
                                                            {cat}
                                                        </option>
                                                    ))}
                                                </select>
                                                {field.state.meta.errors.length > 0 && (
                                                    <p className="text-xs text-red-400">
                                                        {field.state.meta.errors[0]?.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </form.Field>
                                </div>
                            </div>

                            <div className="font-sans text-[20px] font-semibold leading-[100%] text-[#424242]">
                                About the vendor
                            </div>
                            <div className="mt-2 rounded-lg border border-[#c8c8c8] bg-[#eeeeee] p-2">
                                <PostEditor
                                    ref={editorRef}
                                    wrapperClassName="mt-1"
                                    placeholder="Type here about the vendor..."
                                    initialValue={notesContentJson}
                                    onChange={setNotesContentJson}
                                />
                            </div>

                            <div className="mt-6 flex justify-end">
                                <Button
                                    color="blue"
                                    variant="primary"
                                    size="smaller"
                                    isLoading={isLoading || isEditing}
                                    type="submit"
                                    disabled={!isValid || !name || !website || !category}
                                >
                                    <SubmitIcon />
                                </Button>
                            </div>
                        </form>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>

            <VendorAddedModal isOpen={showSuccessModal} setIsOpen={setShowSuccessModal} />
        </Dialog.Root>
    );
};
